import { useForm } from 'react-hook-form'
import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import RichTextEditor from '../Common/RichTextEditor'
import { universityAPI, degreeAPI, specializationAPI } from '../../services/api'

const CourseForm = ({ onSubmit, initialData = null, onCancel, hideUniversitySelect = false, defaultUniversityId = null }) => {
    const { register, handleSubmit, formState: { errors }, watch, setValue, reset } = useForm({
        defaultValues: {}
    })

    const [activeTab, setActiveTab] = useState('basic')
    const [selectedDegreeId, setSelectedDegreeId] = useState(
        initialData?.degreeId ?
            (typeof initialData.degreeId === 'object' ? initialData.degreeId._id : initialData.degreeId)
            : ''
    )

    // Simplified structure to match new Course model
    const [curriculum, setCurriculum] = useState(initialData?.curriculum || '')
    const [eligibility, setEligibility] = useState(initialData?.eligibility || '')
    const [feeStructure, setFeeStructure] = useState(initialData?.feeStructure || '')
    const [scholarships, setScholarships] = useState(initialData?.scholarships || '')

    // Reset form when initialData changes
    useEffect(() => {
        if (initialData) {
            const formData = {
                ...initialData,
                universityId: initialData.universityId ?
                    (typeof initialData.universityId === 'object' ? initialData.universityId._id : initialData.universityId)
                    : '',
                degreeId: initialData.degreeId ?
                    (typeof initialData.degreeId === 'object' ? initialData.degreeId._id : initialData.degreeId)
                    : '',
                specialization: Array.isArray(initialData.specialization) ? initialData.specialization : []
            }
            reset(formData)

            // Explicitly set values using setValue to ensure they're set
            const universityId = initialData.universityId ?
                (typeof initialData.universityId === 'object' ? initialData.universityId._id : initialData.universityId)
                : ''
            const degreeId = initialData.degreeId ?
                (typeof initialData.degreeId === 'object' ? initialData.degreeId._id : initialData.degreeId)
                : ''

            // Use setTimeout to ensure form is fully reset before setting values
            setTimeout(() => {
                if (universityId) {
                    setValue('universityId', universityId)
                }
                if (degreeId) {
                    setValue('degreeId', degreeId)
                    setSelectedDegreeId(degreeId)
                }
            }, 0)

            setCurriculum(initialData.curriculum || '')
            setEligibility(initialData.eligibility || '')
            setFeeStructure(initialData.feeStructure || '')
            setScholarships(initialData.scholarships || '')
        } else {
            reset({})
            setSelectedDegreeId('')
            setCurriculum('')
            setEligibility('')
            setFeeStructure('')
            setScholarships('')
        }
    }, [initialData, reset, setValue])

    // Fetch master data
    const { data: universitiesData } = useQuery({
        queryKey: ['universities'],
        queryFn: () => universityAPI.getUniversities()
    })

    const { data: degreesData } = useQuery({
        queryKey: ['degrees'],
        queryFn: () => degreeAPI.getDegrees()
    })

    const { data: specializationsData } = useQuery({
        queryKey: ['specializations', selectedDegreeId],
        queryFn: () => specializationAPI.getSpecializations(selectedDegreeId ? { degree: selectedDegreeId } : {}),
        enabled: !!selectedDegreeId
    })

    const universities = universitiesData?.data?.universities || []
    const allDegrees = degreesData?.data?.degrees || []
    const allSpecializations = specializationsData?.data?.specializations || []

    // Auto-set duration when degree changes
    useEffect(() => {
        if (selectedDegreeId && allDegrees.length > 0) {
            const selectedDegree = allDegrees.find(d => d._id === selectedDegreeId)
            if (selectedDegree && selectedDegree.duration) {
                setValue('duration', selectedDegree.duration)
            }
        }
    }, [selectedDegreeId, allDegrees, setValue])

    const tabs = [
        { id: 'basic', name: 'Basic Info' },
        { id: 'curriculum', name: 'Curriculum' },
        { id: 'eligibility', name: 'Eligibility' },
        { id: 'fees', name: 'Fee Structure' },
        { id: 'scholarships', name: 'Scholarships' }
    ]

    const handleFormSubmit = (data) => {
        // Fix featured field - convert array to boolean
        const featured = Array.isArray(data.featured) ? data.featured.length > 0 : data.featured

        const formData = {
            ...data,
            featured: featured,
            curriculum: curriculum,
            eligibility: eligibility,
            feeStructure: feeStructure,
            scholarships: scholarships,
            // Handle specialization - keep as array of names
            specialization: Array.isArray(data.specialization) ? data.specialization : [],
            // Keep degreeId as it's now used in the model
            degreeId: data.degreeId || undefined,
            // Set universityId from defaultUniversityId if hideUniversitySelect is true
            universityId: hideUniversitySelect ? defaultUniversityId : (data.universityId || undefined)
        }

        // Remove undefined values
        Object.keys(formData).forEach(key => {
            if (formData[key] === undefined) {
                delete formData[key]
            }
        })

        onSubmit(formData)
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900">
                        {initialData ? 'Edit Course' : 'Add New Course'}
                    </h2>
                    <button
                        onClick={onCancel}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex">
                    {/* Sidebar Tabs */}
                    <div className="w-64 bg-gray-50 border-r border-gray-200">
                        <nav className="p-4 space-y-2">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`w-full text-left px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id
                                        ? 'bg-primary-100 text-primary-700'
                                        : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                >
                                    {tab.name}
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* Form Content */}
                    <div className="flex-1 overflow-y-auto max-h-[calc(90vh-120px)]">
                        <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6 space-y-6">
                            {/* Basic Info Tab */}
                            {activeTab === 'basic' && (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {!hideUniversitySelect && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    University *
                                                </label>
                                                <select
                                                    {...register('universityId', { required: 'University is required' })}
                                                    value={watch('universityId') || ''}
                                                    className="input"
                                                >
                                                    <option value="">Select University</option>
                                                    {universities.map((university) => (
                                                        <option key={university._id} value={university._id}>
                                                            {university.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {errors.universityId && (
                                                    <p className="text-red-500 text-sm mt-1">{errors.universityId.message}</p>
                                                )}
                                            </div>
                                        )}

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Level *
                                            </label>
                                            <select
                                                {...register('level', { required: 'Level is required' })}
                                                value={watch('level') || ''}
                                                className="input"
                                            >
                                                <option value="">Select Level</option>
                                                <option value="Diploma">Diploma</option>
                                                <option value="Undergraduate (UG)">Undergraduate (UG)</option>
                                                <option value="Postgraduate (PG)">Postgraduate (PG)</option>
                                            </select>
                                            {errors.level && (
                                                <p className="text-red-500 text-sm mt-1">{errors.level.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Degree *
                                            </label>
                                            <select
                                                {...register('degreeId', { required: 'Degree is required' })}
                                                value={watch('degreeId') || ''}
                                                onChange={(e) => {
                                                    setSelectedDegreeId(e.target.value)
                                                }}
                                                className="input"
                                            >
                                                <option value="">Select Degree</option>
                                                {allDegrees.map((degree) => (
                                                    <option key={degree._id} value={degree._id}>
                                                        {degree.name}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.degreeId && (
                                                <p className="text-red-500 text-sm mt-1">{errors.degreeId.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Duration *
                                            </label>
                                            <input
                                                type="text"
                                                {...register('duration', { required: 'Duration is required' })}
                                                className="input"
                                                placeholder={selectedDegreeId && allDegrees.find(d => d._id === selectedDegreeId)
                                                    ? allDegrees.find(d => d._id === selectedDegreeId).duration
                                                    : "Select a degree first to see duration"
                                                }
                                                disabled={!selectedDegreeId}
                                            />
                                            {errors.duration && (
                                                <p className="text-red-500 text-sm mt-1">{errors.duration.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Specializations *
                                            </label>
                                            <div className="space-y-2">
                                                {allSpecializations.map((specialization) => (
                                                    <label key={specialization._id} className="flex items-center">
                                                        <input
                                                            type="checkbox"
                                                            value={specialization.name}
                                                            {...register('specialization')}
                                                            className="mr-2"
                                                        />
                                                        <span className="text-sm text-gray-700">{specialization.name}</span>
                                                    </label>
                                                ))}
                                                {allSpecializations.length === 0 && selectedDegreeId && (
                                                    <p className="text-gray-500 text-sm">No specializations available for this degree</p>
                                                )}
                                                {!selectedDegreeId && (
                                                    <p className="text-gray-500 text-sm">Select a degree first to see specializations</p>
                                                )}
                                            </div>
                                            {errors.specialization && (
                                                <p className="text-red-500 text-sm mt-1">{errors.specialization.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Featured Course
                                            </label>
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    {...register('featured')}
                                                    className="mr-2"
                                                />
                                                <span className="text-sm text-gray-700">Mark as featured course</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Curriculum Tab */}
                            {activeTab === 'curriculum' && (
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Curriculum Information *
                                        </label>
                                        <RichTextEditor
                                            value={curriculum}
                                            onChange={setCurriculum}
                                            placeholder="Enter detailed curriculum information..."
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Eligibility Tab */}
                            {activeTab === 'eligibility' && (
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Eligibility Criteria *
                                        </label>
                                        <RichTextEditor
                                            value={eligibility}
                                            onChange={setEligibility}
                                            placeholder="Enter detailed eligibility criteria..."
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Fee Structure Tab */}
                            {activeTab === 'fees' && (
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Fee Structure *
                                        </label>
                                        <RichTextEditor
                                            value={feeStructure}
                                            onChange={setFeeStructure}
                                            placeholder="Enter detailed fee structure information..."
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Scholarships Tab */}
                            {activeTab === 'scholarships' && (
                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Scholarships Information
                                        </label>
                                        <RichTextEditor
                                            value={scholarships}
                                            onChange={setScholarships}
                                            placeholder="Enter scholarship information (optional)..."
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Form Actions */}
                            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={onCancel}
                                    className="btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn-primary"
                                >
                                    {initialData ? 'Update Course' : 'Create Course'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CourseForm